import {AfterViewInit, Component, inject, input, OnInit, PLATFORM_ID, Signal, signal} from '@angular/core';
import {AddressStepComponent} from "../../offer-detail/address-step/address-step.component";
import {BonusStepComponent} from "../../offer-detail/bonus-step/bonus-step.component";
import {MatStep, MatStepLabel, MatStepper} from "@angular/material/stepper";
import {MatVerticalStepperScrollerDirective} from "../../../../core/directives/mat-vertical-stepper-scroller.directive";
import {PaymentStepComponent} from "../../offer-detail/payment-step/payment-step.component";
import {RegistrationStepComponent} from "../../offer-detail/registration-step/registration-step.component";
import {SummaryStepComponent} from "../../offer-detail/summary-step/summary-step.component";
import {TranslationService} from "../../../../core/services/translation.service";
import {Offer} from "../../../../data/models/offer.model";
import {UserData} from "../../../../data/models/user-data.model";
import {STEPPER_GLOBAL_OPTIONS, StepperOrientation, StepperSelectionEvent} from "@angular/cdk/stepper";
import {AddressCombination} from "../../../../data/models/address-combination";
import {OfferCardComponent} from "../offer-card.component";
import {OfferPrice} from "../../../../data/models/price.model";
import {ConfigService} from "../../../../core/services/config.service";
import {NavigationCommand, NavigationService} from "../../../../data/services/navigation.service";
import {isPlatformBrowser} from "@angular/common";
import {OrderResultModel} from "../../../../data/models/orderResult.model";
import {GtmEnum} from "../../../../core/models/gtm.enum";
import {SnackbarService} from "../../../../core/services/snackbar.service";
import {HupSubscriptionsService} from "../../../../data/services/hup-subscriptions.service";
import {OrderTrackingService} from "../../../../core/services/order-tracking.service";
import {LoginService} from "../../../../core/components/login/login.service";
import {OrderProcessingService} from "../../../../data/services/order-processing.service";
import {ScriptLoaderService} from "../../../../core/services/script-loader.service";
import {VisitedSteps} from "../../../../core/utils/visited-steps";
import {ORDER_STEPPER_OPTIONS, stepIndicatorState} from "../../../../core/utils/step-indicator";

@Component({
  selector: 'app-print-offer',
  imports: [
    AddressStepComponent,
    BonusStepComponent,
    MatStep,
    MatStepLabel,
    MatStepper,
    MatVerticalStepperScrollerDirective,
    PaymentStepComponent,
    RegistrationStepComponent,
    SummaryStepComponent,
    OfferCardComponent
  ],
  providers: [{provide: STEPPER_GLOBAL_OPTIONS, useValue: ORDER_STEPPER_OPTIONS}],
  templateUrl: './print-offer.component.html',
  styleUrl: './print-offer.component.css'
})
export class PrintOfferComponent implements OnInit, AfterViewInit {
  protected readonly AddressCombination = AddressCombination;

  private configService = inject(ConfigService);
  protected translationService = inject(TranslationService);
  private snackBar = inject(SnackbarService);
  private subscriptionService = inject(HupSubscriptionsService);
  private orderTrackingService = inject(OrderTrackingService);
  private navigationService = inject(NavigationService);
  private loginService = inject(LoginService);
  private orderProcessService = inject(OrderProcessingService);
  private scriptLoader = inject(ScriptLoaderService);


  public offer = input.required<Offer>();
  public userData = input<UserData>();
  private platformId = inject(PLATFORM_ID);

  selectedBillingAddress = null;
  selectedBonusAddress = null;
  selectedDeliveryAddress = null;

  selectedFormOfPayment = null;
  selectedPaymentFrequency = null;
  selectedStartDate = null;

  offerPrice: OfferPrice[];
  creatingOrder: boolean = false;

  stepperOrientation = input<StepperOrientation>('horizontal');
  isAuthenticated = input(false);
  isEditable = input(false);
  hideLoginBox = input(false);
  isWinback = input(false);

  paymentStepCompleted = signal(false);
  /**
   * Ob eine Online-Zahlart gewählt ist. Wird an den Zusammenfassungs-Schritt durchgereicht, damit
   * der Bestell-Button dort erst nach einer erfolgreichen Zahlung freigegeben wird.
   */
  onlinePaymentSelected = signal(false);
  addressStepCompleted = signal(false);
  registrationStepCompleted = signal(false);
  bonusStepCompleted = signal(false);

  onlyOneAddress = signal(false);

  public currentProductAlreadyOrdered = input(false); //Bezieht der Nutzer das Produkt schon?

  addressCombination = signal(AddressCombination.NONE);

  stepperInitialized = signal(false);

  protected readonly stepIndicatorState = stepIndicatorState;

  private visitedSteps = new VisitedSteps();

  /**
   * Darf der Schritt als erledigt gelten? Erst, wenn der Kunde ihn geöffnet hat - s. VisitedSteps.
   * Ausnahme: Wer das Produkt schon bezieht, springt der Zahlart-Schritt bewusst direkt zur
   * Zusammenfassung (skipDirectlyToLastStep); dafür müssen die Schritte davor als erledigt gelten.
   */
  protected stepReached(step: MatStep, stepper: MatStepper): boolean {
    return this.currentProductAlreadyOrdered() || this.visitedSteps.isVisited(step, stepper);
  }

  stepperSelectionChanged(event: StepperSelectionEvent) {
    this.visitedSteps.track(event);
    // Wenn wir im Stepper zurückgehen, muss der Preis gelöscht werden...
    if (event.previouslySelectedIndex > event.selectedIndex) {
      // this.offerPrice = null;
      // this.showOnlinePayment = false;
      // this.agbChecked.set(false);
    }
  }


  //todo: payment-step / offer-card
  onCompleteOrder(orderResult: OrderResultModel): void {
    this.creatingOrder = true;
    // Nach oben scrollen, damit man den Spinner und die Fehlermeldungen sieht!
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    // this.showOnlinePayment = false;
    // this.errorsArr = [];
    const data = this.orderProcessService.snapshot();
    if (!data.addresses?.email) { //todo: welche E-Mails
      return;
    }
    if (!this.userData() || data.clientNoUrl) {
      this.subscriptionService.sendOrderUnauthorized(data).subscribe({
        next: (res) => {
          this.orderTrackingService.pushEvent(GtmEnum.PURCHASED, res.body);
          this.doUpScoreTracking().catch((err) => console.error('UpScore tracking failed', err));
          const otlcCode = res.otlc ?? '';
          if (otlcCode.length) {
            this.loginService.loginByOtlc(otlcCode);
          }
          // Result an der Session speichern, da wir nichts nachladen können -> User ist noch nicht da ^^
          this.creatingOrder = false;
          const id: number = res.body.webId;
          /* falls direkt nach Paradise geschrieben wird, ist die backendId > 0 und die webId = 0. => Das Abo kann nicht geladen werden und die Bestellbestätigungsseite ist leer.
         Es muss also möglich sein, aus beiden Quellen das Abo zu laden */
          const backendId: string = res.body.backendId > 0 ? res.body.backendId.toString() : '-1';
          this.subscriptionService.setPriceForKey(id, this.offerPrice);
          this.navigationService.navigateTo(NavigationCommand.OFFERORDERED, [id.toString(), backendId, this.offer().orderChoiceKey.toString()]).then((navigated) => {
            if (navigated) {
              this.snackBar.openSnackbar(this.translationService.getTranslation('saved'));
            }
          });
        },
        error: (error) => {
          // const errTestObj = {};  // Warning, wenn einkommentiert
          this.creatingOrder = false;
          console.error(error);
          //this.errorService.handleAPIError([this.firstFormGroup, this.secondFormGroup, this.thirdFormGroup, this.fourthFormGroup, this.fifthFormGroup], errTestObj, this.errorsArr, stepper);
        }
      });
    } else {
      this.subscriptionService.sendOrder(data).subscribe({
        next: (res) => {
          this.orderTrackingService.pushEvent(GtmEnum.PURCHASED, res);
          this.doUpScoreTracking().catch((err) => console.error('UpScore tracking failed', err));
          this.creatingOrder = false;
          const id: number = res.backendId > 0 ? res.backendId : res.webId;
          this.subscriptionService.setPriceForKey(id, this.offerPrice);
          this.navigationService.navigateTo(NavigationCommand.OFFERORDERED, [res.webId, res.backendId, this.offer().orderChoiceKey.toString()]);
        },
        error: (error) => {
          this.creatingOrder = false;
          // const errTestObj = {};  // Warning, wenn einkommentiert
          console.error(error);
          // this.errorService.handleAPIError(fusingTest, error.error, this.errorsArr);
          // this.errorService.handleAPIError([this.complaintForm, this.complaintCompensationForm], error.error, this.errorsArr);
          //this.errorService.handleAPIError([this.firstFormGroup, this.secondFormGroup, this.thirdFormGroup, this.fourthFormGroup, this.fifthFormGroup], errTestObj, this.errorsArr, stepper);
        }
      });
    }
  }

  //todo: payment-step / offer-card
  onOptInsSelected(event) {
  }

  ngOnInit(): void {
    this.configService.loadConfig('offer.onlyOneAddress').subscribe(config => {
      // Nutze setTimeout(0) oder Promise.resolve() um NG0100 zu vermeiden,
      // falls der Config-Load synchron/sofort feuert.
      setTimeout(() => {
        this.onlyOneAddress.set(config && config.value === '1');
        this.defineAddressCombination();
      });
    });
  }

  ngAfterViewInit(): void {
    // Wait one tick so MatStepper can finish its initial index/steps setup.
    setTimeout(() => this.stepperInitialized.set(true));
  }


  /**
   * triggert UpScore
   *
   * Wenn offer.tracking.upScore.domain nicht leer ist, wird UpScore ausgeführt.
   * Der UpScore-Parameter "object_id" wird in den Site-URL-Suchparametern unter dem (Konfig-)Key "offer.tracking.upScore.objectIdKey" gesucht.
   * Der UpScore-Parameter "url" wird in den Site-URL-Suchparametern unter dem (Konfig-)Key "offer.tracking.upScore.urlKey" gesucht.
   */
  async doUpScoreTracking(): Promise<void> {
    const urlParamMap = Object.fromEntries(new URLSearchParams(window.location.search));

    const executorFn = () => {
      // Parameter-Keys bestimmen
      let objectIdKey = this.configService.getConfig('offer.tracking.upScore.objectIdKey')?.value;
      // set Default-Values, falls leer
      objectIdKey = !!objectIdKey ? objectIdKey : 'upScoreId';
      // Parameterwert ermitteln
      let objectId = urlParamMap[objectIdKey] ?? '';

      const data = {object_id: objectId, object_type: 'article', conversion: 2};

      let p;

      if (upScoreVersion == 'v3') {
        p = {
          config: {domain: upScoreDomain, article: 'article', track_positions: true},
          data: data
        };
      } else {
        p = data;
      }

      console.log('%cUpScore-Parameter: ', 'color:orange;', p);

      // @ts-ignore
      upScoreVersion == 'v3' ? upScore(p) : upScore('page_view', p);
    };

    let upScoreDomain = this.configService.getConfig('offer.tracking.upScore.domain')?.value;
    let upScoreVersion = this.configService.getConfig('offer.tracking.upScore.version')?.value ?? 'v4';
    // P5W-3521: SDZC | Anbindung UpScore API für Conversion-Messung
    // mehrere Portale leiten zu einem WebAbo-Shop
    //   1) die Domain wird in diesem Fall als URL-Parameter übergeben (Param:urlParamName), oder
    //   2) ggf. in einem "Sub"-Parameter dieses Parameters (Param:urlParamName->subParamName)
    if (typeof upScoreDomain === 'string' && upScoreDomain.startsWith('Param:')) {
      const upScoreDomainConfRaw = upScoreDomain.substring('Param:'.length);
      const separatorIndex = upScoreDomainConfRaw.indexOf('->');
      const firstLevelParam = separatorIndex >= 0 ? upScoreDomainConfRaw.substring(0, separatorIndex) : upScoreDomainConfRaw;
      const secondLevelParam = separatorIndex >= 0 ? upScoreDomainConfRaw.substring(separatorIndex + 2) : '';
      let upScoreDomainByParam = firstLevelParam ? urlParamMap[firstLevelParam] : null;
      // Falls Parameter im Parameterwert zu finden ist, ...
      if (secondLevelParam.length > 0) {
        // ... 1st-Level Parameter dekodieren, ...
        const firstLevelValue = typeof upScoreDomainByParam === 'string' ? upScoreDomainByParam : '';
        upScoreDomainByParam = decodeURIComponent(firstLevelValue);
        // ... die URL-Parameter ermitteln, ...
        const queryIndex = upScoreDomainByParam.indexOf('?');
        const secondLevelQuery = queryIndex >= 0
          ? upScoreDomainByParam.substring(queryIndex + 1)
          : upScoreDomainByParam;
        const secondLevelUrlParams = Object.fromEntries(new URLSearchParams(secondLevelQuery ?? ''));
        // ... den eingebetteten 2nd-Level Parameter auslesen und schließlich ...
        upScoreDomainByParam = secondLevelUrlParams[secondLevelParam] ?? null;
        // ... den Domain-/Hostnamen ermitteln
        try{
          // a) komplette URL
          upScoreDomainByParam = new URL(upScoreDomainByParam).hostname;
        } catch (e: any) {
          // b) nur Hostname
          try {
            upScoreDomainByParam = new URL('https://' + upScoreDomainByParam).hostname;
          } catch (e: any) {
            // c) leider keins von beiden
            console.error('UpScore-Domain kann nicht ermittelt werden aus URL: ' + (e?.message ?? e));
            upScoreDomainByParam = null;
          }
        }
      }
      upScoreDomain = !!upScoreDomainByParam ? upScoreDomainByParam : null;
      // nur sld + tld ausgeben
      if (typeof upScoreDomain === 'string' && upScoreDomain.length > 0) {
        const domainParts = upScoreDomain.match(/[^.]+/g) ?? [];
        upScoreDomain = domainParts.length >= 2 ? domainParts.slice(-2).join('.') : upScoreDomain;
      } else {
        upScoreDomain = null;
      }
    }

    if (!!upScoreDomain) {
      // @ts-ignore
      if (typeof upScore === 'function') {
        executorFn();
      } else {
        const possibleUpScoreLocations = {
          v3: 'https://files.upscore.com/async/upScore.js',  // v3
          v4: 'https://files.upscore.com/site_script/' +upScoreDomain + '.js' // v4
        };

        const normalizedUpScoreVersion = typeof upScoreVersion === 'string'
          ? upScoreVersion.toLowerCase().trim()
          : 'v4';
        const scriptUrl = possibleUpScoreLocations[normalizedUpScoreVersion] ?? possibleUpScoreLocations.v4;

        try {
          if (!scriptUrl) {
            console.error('UpScore-Script-URL fehlt für Version: ' + normalizedUpScoreVersion);
            return;
          }
          await this.scriptLoader.loadScript(scriptUrl);
          // @ts-ignore
          if (typeof upScore === 'function') {
            executorFn();
            return;
          } else {
            console.error('UpScore-API nicht gefunden (' + scriptUrl + ')');
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
  }

  protected onRegistrationStepCompleted(event: boolean) {
    this.registrationStepCompleted.set(event);
  }

  protected onPaymentStepCompleted(event: boolean) {
    this.paymentStepCompleted.set(event);
  }

  protected onOnlinePaymentSelected(event: boolean) {
    this.onlinePaymentSelected.set(event);
  }

  protected onAddressStepCompleted(event: boolean) {
    this.addressStepCompleted.set(event);
  }

  protected onBonusStepCompleted(event: boolean) {
    this.bonusStepCompleted.set(event);
  }

  defineAddressCombination() {
    // ePaper hat keinen Address-Step (Print-Pfad); die Kombination wird dort separat ermittelt.
    if (this.offer().offerType.ePaper) {
      return;
    }

    const deliveryRequired = this.offer().requiredAddresses.deliveryAddressRequired;
    const billingRequired = this.offer().requiredAddresses.billingAddressRequired;

    if (!deliveryRequired && !billingRequired) {
      this.addressCombination.set(AddressCombination.NONE);
      return;
    }

    // Wenn nur eine Adresse abgefragt werden soll, bevorzugen wir die Lieferadresse
    // (typischer Print-Fall: Abo benötigt Liefer- und Rechnungsadresse, es wird aber nur eine erfasst).
    if (this.onlyOneAddress()) {
      this.addressCombination.set(deliveryRequired ? AddressCombination.ONLY_DELIVERY : AddressCombination.ONLY_BILLING);
      return;
    }

    if (deliveryRequired && billingRequired) {
      this.addressCombination.set(AddressCombination.BOTH);
    } else if (deliveryRequired) {
      this.addressCombination.set(AddressCombination.ONLY_DELIVERY);
    } else {
      this.addressCombination.set(AddressCombination.ONLY_BILLING);
    }
  }
}

